type Element = {
	width: number;
	height: number;
};

export type SizeCalculatorFn<T extends Element> = (element: T) => number;

export type CalculatorSettings<T extends Element> = {
	getWidth: SizeCalculatorFn<T>;
	getFlexGrow: SizeCalculatorFn<T>;
	getMaxWidth?: SizeCalculatorFn<T>;
};

export class SizeCalculator<T extends Element> {
	private getWidth: SizeCalculatorFn<T>;
	private getMaxWidth: SizeCalculatorFn<T>;
	private getFlexGrow: SizeCalculatorFn<T>;

	constructor({ getWidth, getMaxWidth, getFlexGrow }: CalculatorSettings<T>) {
		this.getWidth = getWidth;
		this.getMaxWidth = getMaxWidth;
		this.getFlexGrow = getFlexGrow;
	}

	public getElementRatio(element: T) {
		return element.width / element.height;
	}

	public calculateHeight(element: T, newWidth: number) {
		const ratio = this.getElementRatio(element);
		return newWidth / ratio;
	}

	public getComputedWidth(element: T, proposedWidth: number) {
		return !!this.getMaxWidth ? Math.min(proposedWidth, this.getMaxWidth(element)) : proposedWidth;
	}

	public buildAllRows(elements: T[], containerWidth: number, gutterValue: number = 0): T[][] {
		const result = [];
		const screenWidth = containerWidth; // Can be overriden if there is a scrollbar for example
		let row = [];
		let remainingWidth = screenWidth;
		for (const image of elements) {
			const newWidth = this.getComputedWidth(image, this.getWidth(image)) + gutterValue;

			if (remainingWidth - newWidth <= 0) {
				result.push(row);
				row = [image];
				remainingWidth = screenWidth - newWidth;
			} else {
				row.push(image);
				// Handle too big for width
				remainingWidth = remainingWidth - newWidth;
			}
		}
		result.push(row);
		return result;
	}

	public computeRowHeight(elements: T[], containerWidth: number, additionalHeight = 0): number {
		let elementsWidth = 0;
		let totalFlexGrow = 0;
		if (elements.length === 0) return 0;
		elements.forEach(element => {
			elementsWidth += this.getWidth(element);
			totalFlexGrow += this.getFlexGrow(element);
		});
		const remainingSpace = containerWidth - elementsWidth;
		const resizedElements = elements.map(element => {
			const newWidth = this.calculateElementFlexWidth(element, remainingSpace, totalFlexGrow);
			const newHeight = this.calculateHeight(element, newWidth);
			return { width: newWidth, height: newHeight };
		});
		const maxHeight = Math.max(...resizedElements.map(element => element.height));
		return maxHeight + additionalHeight;
	}

	public calculateElementFlexWidth(element: T, remainingSpace: number, totalFlexGrow: number) {
		const width = this.getWidth(element);
		const flexGrow = this.getFlexGrow(element);
		const flexElementWidth = (flexGrow / totalFlexGrow) * remainingSpace + width;
		return this.getComputedWidth(element, flexElementWidth);
	}
}
